import React, { useCallback, useRef, useEffect, useState } from 'react'
import { Box, LinearProgress } from '@mui/material'
import {
    GoogleMap,
    Libraries,
    MarkerF,
    useJsApiLoader,
} from '@react-google-maps/api'
import { SitesTable } from './SitesTable'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import {
    EntityStatus,
    FMTABS,
    googleMapOptions,
    URLSearchParamsKeys,
    USACenter,
} from '../../../../constants/constants'
import { useSearchParams } from 'react-router-dom'
import {
    setSiteList,
    setSiteBuildingAssetCount,
    updateHierarchyLevelData,
} from '../../../../redux/slices/facilityMonitoringSlice'
import {
    fetchEntityRecordsList,
    getBuildingsAssetsCount,
} from '../../../../services/entityServices'
import { IEntityTableDefinition } from '../../../Entity/interfaces'
import { prepareSiteList } from '../Utils/FMUtils'

const libraries: Libraries = ['places']

const MapView: React.FC = () => {
    const fmSlice = useAppSelector((state) => state.facilityMonitoring)
    const {
        siteList,
        siteLocationAttributeId,
        siteNameAttributeId,
        siteBuildingAssetCount,
        hierarchyConfig,
        siteEntityTemplateId,
    } = fmSlice
    const orgSlice = useAppSelector((state) => state.org)
    const { org } = orgSlice

    const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    })
    const mapRef: any = useRef(null)
    const [, setSearchParams] = useSearchParams()
    const [isLoading, setIsLoading] = useState(false)

    const handleMarkerClick = useCallback(
        (selectedSiteUID: string) => {
            const site = siteList.find((s) => s.uid === selectedSiteUID)
            const additionalParams: Record<string, string> = {}

            if (site) {
                const assetCountNode = siteBuildingAssetCount.find(
                    (node) => node.siteId === site.id,
                )
                if (assetCountNode?.parentRelations) {
                    hierarchyConfig.forEach((config, index) => {
                        const match = assetCountNode.parentRelations.find(
                            (rel) =>
                                rel.entityTemplateId ===
                                config.entityTemplateId,
                        )
                        if (match) {
                            if (
                                config.entityTemplateId === siteEntityTemplateId
                            ) {
                                additionalParams[
                                    URLSearchParamsKeys.selectedSiteUID
                                ] = match.uid
                            } else {
                                additionalParams[
                                    `${URLSearchParamsKeys.dynamicLevelPrefix}${index}`
                                ] = match.uid
                            }
                        }
                    })
                }
            }

            setSearchParams({
                ...additionalParams,
                selectedSiteUID,
                [URLSearchParamsKeys.tabIndex]: FMTABS.SITE_VIEW.toString(),
            })
        },
        [
            siteList,
            siteEntityTemplateId,
            siteBuildingAssetCount,
            hierarchyConfig,
            setSearchParams,
        ],
    )

    const renderMarkers = useCallback(() => {
        return siteList.map((marker) => (
            <MarkerF
                key={marker.id}
                position={marker.position}
                onClick={() => handleMarkerClick(marker.uid)}
                // icon={'assets/images/temp/marker-red.png'}
            />
        ))
    }, [siteList, handleMarkerClick])

    const onLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map
            const bounds = new google.maps.LatLngBounds()
            siteList.forEach((marker) => {
                bounds.extend(
                    new google.maps.LatLng(
                        marker.position.lat,
                        marker.position.lng,
                    ),
                )
            })
            if (siteList && siteList?.length > 0) {
                mapRef.current.fitBounds(bounds)
            } else {
                mapRef.current.setCenter(USACenter)
                mapRef.current.setZoom(2)
            }
        },
        [siteList],
    )

    useEffect(() => {
        if (mapRef.current) {
            const bounds = new google.maps.LatLngBounds()
            siteList.forEach((marker) => {
                bounds.extend(
                    new google.maps.LatLng(
                        marker.position.lat,
                        marker.position.lng,
                    ),
                )
            })
            mapRef.current.fitBounds(bounds)
        }
    }, [siteList])

    const dispatch = useAppDispatch()

    const reloadSiteList = useCallback(async () => {
        setIsLoading(true)

        const queryParams = {
            filterBy: [EntityStatus.ACTIVE],
            filters: [],
        }
        const siteRecords: IEntityTableDefinition =
            await fetchEntityRecordsList(
                org?.id || '',
                siteEntityTemplateId,
                queryParams,
            )

        const newSiteList = prepareSiteList(
            siteRecords,
            siteLocationAttributeId,
            siteNameAttributeId,
        )
        const siteBuildingAssetCount = await getBuildingsAssetsCount(
            org?.id || '',
            true,
        )
        dispatch(setSiteList(newSiteList))
        dispatch(setSiteBuildingAssetCount(siteBuildingAssetCount))

        if (hierarchyConfig?.[0]?.entityTemplateId === siteEntityTemplateId) {
            const rootEntities = siteRecords.data.map((item: any) => ({
                id: item.entityId,
                entityInstanceId: item.entityId,
                templateId: siteEntityTemplateId,
                attributes: item.attributes,
                displayName: item.uid || '',
                uid: item.uid || '',
                status: item.status,
                permissionLevel: item.action,
            }))
            dispatch(
                updateHierarchyLevelData({
                    levelIndex: 0,
                    data: { entities: rootEntities as any },
                }),
            )
        }

        setIsLoading(false)
    }, [
        org?.id,
        siteEntityTemplateId,
        siteLocationAttributeId,
        siteNameAttributeId,
        dispatch,
        hierarchyConfig,
    ])

    const onUnmount = useCallback(() => {
        mapRef.current = null
    }, [])

    if (!isLoaded) {
        return <LinearProgress />
    }

    return (
        <Box
            sx={{
                display: 'flex',
                height: '100%',
                opacity: isLoading ? 0.5 : 1,
            }}
        >
            <Box sx={{ width: '65%' }}>
                <GoogleMap
                    mapContainerStyle={{
                        width: '100%',
                        height: '100%',
                    }}
                    center={mapRef.current?.getCenter()}
                    zoom={10}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    options={googleMapOptions}
                >
                    {renderMarkers()}
                </GoogleMap>
            </Box>
            <SitesTable
                siteList={siteList}
                handleMarkerClick={handleMarkerClick}
                reloadSiteList={reloadSiteList}
            />
        </Box>
    )
}

export default MapView
